import { Ray, Vec3 } from 'playcanvas';

import { Events } from './events';
import { Scene } from './scene';

type AnnotationCamera = {
    position: [number, number, number];
    target: [number, number, number];
    fov?: number;
};

type Annotation = {
    id: number;
    title: string;
    text: string;
    link: string;
    image: string;              // optional data-URL image shown in the hotspot card
    camera: AnnotationCamera | null; // optional curated "view from here" framing
    position: Vec3;
};

let nextId = 1;

// work globals
const ray = new Ray();
const screen = new Vec3();

/**
 * Annotation / hotspot system. Lets the user place clickable markers in the
 * scene, each with a title, description, optional link/image and an optional
 * curated camera view. Annotations are rendered as numbered markers in the
 * viewport, listed/edited in the scene panel, persisted into .ssproj documents,
 * exported into the viewer, and fully undoable via the app's edit history.
 */
const TOUR_INTERVAL_MS = 6000;

const registerAnnotationsEvents = (events: Events, scene: Scene) => {
    const annotations: Annotation[] = [];
    let placing = false;
    let selectedId = -1;
    let selectedIds = new Set<number>();

    // guided tour / play mode (view mode only): auto-advances through
    // annotations in list order, showing each one's card + camera framing.
    let tourPlaying = false;
    let tourIndex = -1;
    let tourTimer: ReturnType<typeof setTimeout> = null;

    // viewport marker overlay
    const overlay = document.createElement('div');
    overlay.id = 'annotation-overlay';
    overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:1;';
    document.getElementById('canvas-container')?.appendChild(overlay);

    const markers = new Map<number, HTMLElement>();

    // hotspot popup card (view mode)
    let card: HTMLElement = null;
    const closeCard = () => {
        if (card) {
            card.remove();
            card = null;
        }
    };
    const showCard = (a: Annotation, x?: number, y?: number) => {
        closeCard();
        card = document.createElement('div');
        card.id = 'hotspot-card';

        const close = document.createElement('div');
        close.className = 'hotspot-card-close';
        close.textContent = '×';
        close.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            closeCard();
        });
        card.appendChild(close);

        const title = document.createElement('div');
        title.className = 'hotspot-card-title';
        title.textContent = a.title;
        card.appendChild(title);

        if (a.image) {
            const img = document.createElement('img');
            img.className = 'hotspot-card-image';
            img.src = a.image;
            img.alt = a.title;
            card.appendChild(img);
        }

        if (a.text) {
            const text = document.createElement('p');
            text.className = 'hotspot-card-text';
            text.textContent = a.text;
            card.appendChild(text);
        }

        if (a.link) {
            const link = document.createElement('a');
            link.className = 'hotspot-card-link';
            link.href = a.link;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.textContent = 'Learn more';
            card.appendChild(link);
        }

        document.body.appendChild(card);

        // position near the click, clamped to the viewport. When no click
        // point is given (e.g. guided tour auto-advance), center the card
        // near the bottom of the viewport instead.
        const rect = card.getBoundingClientRect();
        let px: number;
        let py: number;
        if (x === undefined || y === undefined) {
            px = (window.innerWidth - rect.width) / 2;
            py = window.innerHeight - rect.height - 96;
        } else {
            px = x + 14;
            py = y + 14;
            if (px + rect.width > window.innerWidth) px = x - rect.width - 14;
            if (py + rect.height > window.innerHeight) py = y - rect.height - 14;
        }
        card.style.left = `${Math.max(8, px)}px`;
        card.style.top = `${Math.max(8, py)}px`;
    };

    const markerBaseStyle = (selected: boolean) => `
        position:absolute; transform:translate(-50%,-50%);
        min-width:22px; height:22px; padding:0 6px; box-sizing:border-box;
        display:flex; align-items:center; justify-content:center;
        border-radius:9999px; cursor:pointer; pointer-events:auto;
        font-family:Arial,Helvetica,sans-serif; font-size:12px; font-weight:700;
        color:#fff; border:2px solid #fff;
        background:${selected ? '#A78BFA' : '#7C3AED'};
        box-shadow:0 2px 8px rgba(0,0,0,0.45);
        transition:background 0.15s ease, transform 0.05s ease;
    `;

    const createMarker = (a: Annotation) => {
        const el = document.createElement('div');
        el.className = 'annotation-marker';
        el.style.cssText = markerBaseStyle(false);
        el.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            if (events.invoke('viewMode')) {
                // view mode: present the hotspot content + focus the camera
                events.fire('annotations.flyTo', a.id);
                showCard(a, e.clientX, e.clientY);
            } else if (e.shiftKey || e.ctrlKey || e.metaKey) {
                events.fire('annotations.toggleSelect', a.id);
            } else {
                // editor mode: select + focus, and preview the same hotspot
                // card the published viewer would show
                events.fire('annotations.select', a.id);
                events.fire('annotations.flyTo', a.id);
                showCard(a, e.clientX, e.clientY);
            }
        });
        overlay.appendChild(el);
        markers.set(a.id, el);
    };

    const removeMarker = (id: number) => {
        const el = markers.get(id);
        if (el) {
            el.remove();
            markers.delete(id);
        }
    };

    // keep marker numbers/labels/highlight in sync with the list order + selection
    const refreshMarkers = () => {
        annotations.forEach((a, i) => {
            const el = markers.get(a.id);
            if (el) {
                el.textContent = String(i + 1);
                el.title = a.title;
                el.style.cssText = markerBaseStyle(selectedIds.has(a.id));
            }
        });
    };

    const changed = () => {
        refreshMarkers();
        events.fire('annotations.changed');
    };

    // low-level mutators (the "do"/"undo" primitives). These never touch edit
    // history themselves — the public API below wraps them in undoable ops.
    const applyAdd = (a: Annotation) => {
        annotations.push(a);
        createMarker(a);
        selectedId = a.id;
        selectedIds = new Set([a.id]);
        changed();
    };

    const applyRemoveAt = (index: number) => {
        const [a] = annotations.splice(index, 1);
        if (a) {
            removeMarker(a.id);
            selectedIds.delete(a.id);
            if (selectedId === a.id) {
                selectedId = selectedIds.size ? Array.from(selectedIds)[0] : -1;
            }
            changed();
        }
    };

    const applyInsertAt = (index: number, a: Annotation) => {
        annotations.splice(index, 0, a);
        createMarker(a);
        changed();
    };

    const applyUpdate = (id: number, fields: Partial<Annotation>) => {
        const a = annotations.find(v => v.id === id);
        if (a) {
            Object.assign(a, fields);
            changed();
        }
    };

    // undoable edit-history ops. Kept local to this module since they close
    // over the mutators above.
    class AnnotationAddOp {
        name = 'annotationAdd';
        annotation: Annotation;

        constructor(annotation: Annotation) {
            this.annotation = annotation;
        }

        do() { applyAdd(this.annotation); }
        undo() {
            const idx = annotations.indexOf(this.annotation);
            if (idx !== -1) applyRemoveAt(idx);
        }
    }

    class AnnotationRemoveOp {
        name = 'annotationRemove';
        annotation: Annotation;
        index: number;

        constructor(annotation: Annotation, index: number) {
            this.annotation = annotation;
            this.index = index;
        }

        do() {
            const idx = annotations.indexOf(this.annotation);
            if (idx !== -1) applyRemoveAt(idx);
        }
        undo() { applyInsertAt(this.index, this.annotation); }
    }

    // batch remove: items must be captured with their ORIGINAL indices, sorted
    // descending, so `do()` can remove highest-index-first without invalidating
    // the remaining captured indices, and `undo()` reinserts lowest-index-first
    // to restore the original order.
    class AnnotationRemoveManyOp {
        name = 'annotationRemoveMany';
        items: { annotation: Annotation, index: number }[];

        constructor(items: { annotation: Annotation, index: number }[]) {
            this.items = items;
        }

        do() {
            for (const { annotation } of this.items) {
                const idx = annotations.indexOf(annotation);
                if (idx !== -1) applyRemoveAt(idx);
            }
        }
        undo() {
            for (let i = this.items.length - 1; i >= 0; i--) {
                const { annotation, index } = this.items[i];
                applyInsertAt(index, annotation);
            }
        }
    }

    class AnnotationUpdateOp {
        name = 'annotationUpdate';
        id: number;
        oldFields: Partial<Annotation>;
        newFields: Partial<Annotation>;

        constructor(id: number, oldFields: Partial<Annotation>, newFields: Partial<Annotation>) {
            this.id = id;
            this.oldFields = oldFields;
            this.newFields = newFields;
        }

        do() { applyUpdate(this.id, this.newFields); }
        undo() { applyUpdate(this.id, this.oldFields); }
    }

    const add = (position: Vec3, title?: string) => {
        const a: Annotation = {
            id: nextId++,
            title: title ?? `Annotation ${annotations.length + 1}`,
            text: '',
            link: '',
            image: '',
            camera: null,
            position: position.clone()
        };
        events.fire('edit.add', new AnnotationAddOp(a));
        return a;
    };

    const remove = (id: number) => {
        const idx = annotations.findIndex(a => a.id === id);
        if (idx !== -1) {
            events.fire('edit.add', new AnnotationRemoveOp(annotations[idx], idx));
        }
    };

    const removeMany = (ids: number[]) => {
        const items = ids
        .map(id => ({ annotation: annotations.find(a => a.id === id), index: annotations.findIndex(a => a.id === id) }))
        .filter(item => item.annotation && item.index !== -1)
        .sort((x, y) => y.index - x.index);
        if (items.length) {
            events.fire('edit.add', new AnnotationRemoveManyOp(items));
        }
    };

    const update = (id: number, fields: Partial<Annotation>) => {
        const a = annotations.find(v => v.id === id);
        if (a) {
            const oldFields: Partial<Annotation> = {};
            (Object.keys(fields) as (keyof Annotation)[]).forEach((key) => {
                (oldFields as any)[key] = a[key];
            });
            events.fire('edit.add', new AnnotationUpdateOp(id, oldFields, fields));
        }
    };

    // clear is used for whole-document resets (new doc / load) and is
    // intentionally NOT undoable, matching scene.clear()'s own behavior.
    const clear = () => {
        annotations.length = 0;
        markers.forEach(el => el.remove());
        markers.clear();
        selectedId = -1;
        selectedIds = new Set();
        closeCard();
        changed();
    };

    // update marker screen positions every render
    events.on('prerender', () => {
        if (annotations.length === 0) {
            return;
        }
        const camera = scene.camera.mainCamera.camera;
        annotations.forEach((a) => {
            const el = markers.get(a.id);
            if (!el) {
                return;
            }
            camera.worldToScreen(a.position, screen);
            // hide markers that are behind the camera
            if (screen.z <= 0) {
                el.style.display = 'none';
            } else {
                el.style.display = 'flex';
                el.style.left = `${screen.x}px`;
                el.style.top = `${screen.y}px`;
            }
        });
    });

    // placement mode: next viewport click drops an annotation at the picked point
    const setPlacing = (value: boolean) => {
        placing = value;
        document.body.style.cursor = value ? 'crosshair' : '';
        events.fire('annotations.placingChanged', value);
    };

    scene.canvas.addEventListener('pointerdown', async (e: PointerEvent) => {
        // clicking the scene dismisses any open hotspot card
        closeCard();
        if (!placing || e.button !== 0) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();

        const rect = scene.canvas.getBoundingClientRect();
        const nx = (e.clientX - rect.left) / rect.width;
        const ny = (e.clientY - rect.top) / rect.height;

        // try to pick a point on the scene; fall back to the ground plane / in front of camera
        let position: Vec3 | null = null;
        try {
            const hit = await scene.camera.intersect(nx, ny);
            if (hit) {
                position = hit.position;
            }
        } catch (err) {
            // ignore pick failures, use fallback below
        }

        if (!position) {
            scene.camera.getRay(e.clientX - rect.left, e.clientY - rect.top, ray);
            if (Math.abs(ray.direction.y) > 1e-4) {
                const t = -ray.origin.y / ray.direction.y;
                position = t > 0 ?
                    new Vec3().copy(ray.origin).add(new Vec3().copy(ray.direction).mulScalar(t)) :
                    null;
            }
            if (!position) {
                position = new Vec3().copy(ray.origin).add(new Vec3().copy(ray.direction).mulScalar(5));
            }
        }

        add(position);
        setPlacing(false);
    }, true);

    // public API
    events.function('annotations.list', () => annotations);
    events.function('annotations.placing', () => placing);
    events.function('annotations.selected', () => selectedId);
    events.function('annotations.selectedIds', () => Array.from(selectedIds));

    events.on('annotations.startPlacing', () => {
        // Render mode has no editing chrome or gizmos; placement must not start there.
        if (events.invoke('workspace.mode') === 'render') {
            return;
        }
        setPlacing(true);
    });
    events.on('annotations.stopPlacing', () => setPlacing(false));

    // cancel any in-progress placement when entering Render mode
    events.on('workspace.mode', (mode: string) => {
        if (mode === 'render' && placing) {
            setPlacing(false);
        }
    });
    events.on('annotations.add', (position: Vec3, title?: string) => add(position, title));
    events.on('annotations.remove', (id: number) => remove(id));
    events.on('annotations.removeMany', (ids: number[]) => removeMany(ids));
    events.on('annotations.deleteSelected', () => {
        const ids = selectedIds.size ? Array.from(selectedIds) : (selectedId !== -1 ? [selectedId] : []);
        removeMany(ids);
    });
    events.on('annotations.update', (id: number, fields: Partial<Annotation>) => update(id, fields));

    events.on('annotations.select', (id: number) => {
        selectedId = id;
        selectedIds = new Set([id]);
        refreshMarkers();
        events.fire('annotations.selectionChanged', id);
    });

    events.on('annotations.toggleSelect', (id: number) => {
        if (selectedIds.has(id)) {
            selectedIds.delete(id);
            if (selectedId === id) {
                selectedId = selectedIds.size ? Array.from(selectedIds)[Array.from(selectedIds).length - 1] : -1;
            }
        } else {
            selectedIds.add(id);
            selectedId = id;
        }
        refreshMarkers();
        events.fire('annotations.selectionChanged', selectedId);
    });

    events.on('annotations.flyTo', (id: number) => {
        const a = annotations.find(v => v.id === id);
        if (!a) {
            return;
        }
        if (a.camera) {
            events.fire('camera.setPose', {
                position: new Vec3(a.camera.position),
                target: new Vec3(a.camera.target),
                fov: a.camera.fov
            });
        } else {
            scene.camera.setFocalPoint(a.position);
        }
    });

    // guided tour: visits each annotation in order, showing its card + camera
    // framing, then auto-advancing after TOUR_INTERVAL_MS.
    const clearTourTimer = () => {
        if (tourTimer !== null) {
            clearTimeout(tourTimer);
            tourTimer = null;
        }
    };

    const scheduleTourAdvance = () => {
        clearTourTimer();
        if (tourPlaying) {
            tourTimer = setTimeout(() => tourGoTo(tourIndex + 1, true), TOUR_INTERVAL_MS);
        }
    };

    const tourGoTo = (index: number, autoAdvance: boolean) => {
        if (annotations.length === 0) {
            return;
        }
        tourIndex = ((index % annotations.length) + annotations.length) % annotations.length;
        const a = annotations[tourIndex];
        events.fire('annotations.flyTo', a.id);
        showCard(a);
        events.fire('annotations.tourChanged', { index: tourIndex, total: annotations.length, playing: tourPlaying });
        if (autoAdvance) {
            scheduleTourAdvance();
        } else {
            clearTourTimer();
        }
    };

    events.on('annotations.tourStart', () => {
        if (annotations.length === 0) {
            return;
        }
        tourPlaying = true;
        tourGoTo(0, true);
    });

    events.on('annotations.tourStop', () => {
        tourPlaying = false;
        tourIndex = -1;
        clearTourTimer();
        closeCard();
        events.fire('annotations.tourChanged', null);
    });

    events.on('annotations.tourTogglePause', () => {
        if (tourIndex === -1) {
            return;
        }
        tourPlaying = !tourPlaying;
        if (tourPlaying) {
            scheduleTourAdvance();
        } else {
            clearTourTimer();
        }
        events.fire('annotations.tourChanged', { index: tourIndex, total: annotations.length, playing: tourPlaying });
    });

    events.on('annotations.tourNext', () => {
        if (tourIndex !== -1) {
            tourGoTo(tourIndex + 1, tourPlaying);
        }
    });

    events.on('annotations.tourPrev', () => {
        if (tourIndex !== -1) {
            tourGoTo(tourIndex - 1, tourPlaying);
        }
    });

    events.function('annotations.tourState', () => tourIndex === -1 ? null : { index: tourIndex, total: annotations.length, playing: tourPlaying });

    events.on('scene.clear', clear);

    // serialization (.ssproj)
    events.function('docSerialize.annotations', () => {
        return annotations.map(a => ({
            title: a.title,
            text: a.text,
            link: a.link,
            image: a.image,
            camera: a.camera,
            position: [a.position.x, a.position.y, a.position.z]
        }));
    });

    events.function('docDeserialize.annotations', (data: any[]) => {
        clear();
        (data ?? []).forEach((d) => {
            const a: Annotation = {
                id: nextId++,
                title: d.title ?? 'Annotation',
                text: d.text ?? '',
                link: d.link ?? '',
                image: d.image ?? '',
                camera: d.camera ?? null,
                position: new Vec3(d.position)
            };
            annotations.push(a);
            createMarker(a);
        });
        selectedId = -1;
        selectedIds = new Set();
        changed();
    });

    // fallback camera pose for hotspots without a curated "Save current view"
    // (the external viewer's Annotation.camera is a REQUIRED, non-null field —
    // see apps/supersplat-viewer schemas/v2.ts's validateAnnotation)
    const defaultCameraPose = (position: Vec3): AnnotationCamera => ({
        position: [position.x, position.y + 1.2, position.z + 3],
        target: [position.x, position.y, position.z],
        fov: 60
    });

    // export shape for the viewer (ExperienceSettings.annotations). Matches
    // apps/supersplat-viewer's schemas/v2.ts `Annotation` type exactly: camera
    // must be `{ initial: { position, target, fov } }`, never null/flat.
    events.function('annotations.export', () => {
        return annotations.map((a) => {
            const pose = a.camera ?? defaultCameraPose(a.position);
            return {
                position: [a.position.x, a.position.y, a.position.z] as [number, number, number],
                title: a.title,
                text: a.text,
                extras: {
                    ...(a.link ? { link: a.link } : {}),
                    ...(a.image ? { image: a.image } : {})
                },
                camera: {
                    initial: {
                        position: pose.position,
                        target: pose.target,
                        fov: pose.fov ?? 60
                    }
                }
            };
        });
    });
};

export { registerAnnotationsEvents, Annotation, AnnotationCamera };
